import { CountryService, type CountryVO } from '@/api/basic-data/country.service'
import type { UniversalSearchItem } from '@/api/universal-search'
import { getCurrentLocale } from '@/i18n'
import { useAuthStore } from '@/stores/auth'

// 仅合并同一用户、租户内并发的列表请求；请求结束即释放，不缓存旧国家数据。
const pendingLists = new Map<string, Promise<CountryVO[]>>()

function tenantCountryScope(): string {
  const user = useAuthStore().userInfo
  return `${user?.tenantId}:${user?.id}`
}

/** 同屏多个国家字段共用一次在途查询，租户切换后不会复用旧请求。 */
export function loadTenantCountries(): Promise<CountryVO[]> {
  const key = tenantCountryScope()
  let pending = pendingLists.get(key)
  if (!pending) {
    pending = CountryService.queryList({}).finally(() => { pendingLists.delete(key) })
    pendingLists.set(key, pending)
  }
  return pending
}

/** 平台资料筛选保留完整平台范围：合并已关联与待关联的实时记录，仅按 ID 去重。 */
export async function loadPlatformCountriesForFilter(): Promise<CountryVO[]> {
  const key = `${tenantCountryScope()}:platform`
  let pending = pendingLists.get(key)
  if (!pending) {
    // queryPlatformList 只返回尚未关联的国家，不能单独用它替代原平台筛选。
    pending = Promise.all([loadTenantCountries(), CountryService.queryPlatformList({})])
      .then(lists => [...new Map(lists.flat().map(row => [String(row.id), row])).values()])
      .finally(() => { pendingLists.delete(key) })
    pendingLists.set(key, pending)
  }
  return pending
}

/** 为当前表单的国家联动提供租户校验，保存时可等待全部在途解析完成。 */
export function createTenantCountryLookup() {
  const pending = new Set<Promise<string | undefined>>()
  return {
    /** 仅接受有效租户国家的 ID 或 ISO 代码，不把历史删除 ID 替换成同名新 ID。 */
    resolveId(value: unknown): Promise<string | undefined> {
      const text = String(value ?? '').trim()
      if (!text) return Promise.resolve(undefined)
      const scope = tenantCountryScope()
      const request = loadTenantCountries().then(rows => {
        if (scope !== tenantCountryScope()) return undefined
        const country = rows.find(row => row.enabled === 1 && (/^\d+$/.test(text)
          ? String(row.id) === text
          : row.countryCode?.toUpperCase() === text.toUpperCase()))
        return country?.id ? String(country.id) : undefined
      })
      pending.add(request)
      // 同时注册成功和失败清理，避免 finally 派生未处理的拒绝 Promise。
      void request.then(() => pending.delete(request), () => pending.delete(request))
      return request
    },
    /** 调用方在组装保存参数前等待，联动回调先于此 Promise 完成。 */
    async wait(): Promise<void> {
      while (pending.size) await Promise.all([...pending])
    }
  }
}

/** 国家选项保留原始主键和双语名称，供指定业务页面使用。 */
export function toTenantCountryItem(country: CountryVO): UniversalSearchItem {
  return {
    id: String(country.id),
    code: country.countryCode,
    nameCn: country.countryNameCn,
    nameEn: country.countryNameEn,
    enabled: country.enabled,
    content: { ...country }
  }
}

/** 从现有租户国家接口获取有效候选，避免通用搜索的历史缓存混入。 */
export async function searchTenantCountryItems(keyword = ''): Promise<UniversalSearchItem[]> {
  const rows = await CountryService.queryList({ enabled: 1, keyword: keyword.trim() || undefined })
  const byId = new Map<string, UniversalSearchItem>()
  rows.filter(row => row.id && (row.countryNameCn || row.countryNameEn || row.countryCode))
    .forEach(row => byId.set(String(row.id), toTenantCountryItem(row)))
  return [...byId.values()]
}

/** 在显示时读取当前语言，语言切换不改变已保存的国家 ID 或代码。 */
export function tenantCountryLabel(item: any): string {
  const country = item?.content || item || {}
  const cn = item?.nameCn || country.countryNameCn || country.nameCn
  const en = item?.nameEn || country.countryNameEn || country.nameEn
  return String((getCurrentLocale() === 'zh-CN' ? cn || en : en || cn)
    || item?.label || item?.code || country.countryCode || item?.value || item?.id || '')
}

/** 为业务列表筛选提供远程候选，值字段沿用各筛选原有契约。 */
export function tenantCountryFilter(valueField = 'id') {
  return {
    isPlatformData: false,
    allowEmptyRemoteSearch: true,
    displayFormat: { formatter: tenantCountryLabel },
    remoteSearch: async(keyword: string) => (await searchTenantCountryItems(keyword)).map(item => ({
      ...item,
      value: item[valueField as keyof UniversalSearchItem],
      label: tenantCountryLabel(item)
    }))
  }
}

/** 原平台范围的查询字段仅排除删除记录，不收窄到当前租户已关联国家。 */
export function platformCountryFilter(valueField = 'id') {
  return {
    ...tenantCountryFilter(valueField),
    isPlatformData: true,
    remoteSearch: async(keyword: string) => {
      const query = keyword.trim().toLowerCase()
      return (await loadPlatformCountriesForFilter())
        .filter(row => [row.countryCode, row.countryNameCn, row.countryNameEn]
          .some(value => String(value || '').toLowerCase().includes(query)))
        .map(toTenantCountryItem)
        .map(item => ({ ...item, value: item[valueField as keyof UniversalSearchItem], label: tenantCountryLabel(item) }))
    }
  }
}
